/**
 * Marks a Cognito session that exists only to complete an account-setup
 * challenge, not because anyone logged in.
 *
 * `confirmSignIn` on the NEW_PASSWORD_REQUIRED challenge establishes a real
 * session and fires Amplify's `signedIn` Hub event, even though the flow that
 * owns it signs straight back out and sends the user to /login to sign in
 * properly. Every listener that reads `signedIn` as "a user arrived" races that
 * sign-out: security-lifecycle warms the DEK and the reference-data cache, the
 * `device_verification_required` 403s those calls get drive AuthInterceptor to
 * the device OTP screen, and that screen's first call goes out after the tokens
 * are already gone.
 *
 * Seen on staging 2026-10-03: POST /auth/devices/check returned 401 "Missing
 * authorization header" in the same second that GET /auth/me returned 403 for a
 * valid session, leaving the user on "Verify your device" with the raw backend
 * string printed under the code boxes.
 *
 * Flows that own such a session wrap it in `withTransientAuthSession`; the
 * listeners above stand down until a real login happens.
 */

// Counted rather than boolean: a setup flow signs out, signs in and confirms
// inside one window, and a nested caller must not end the outer caller's.
let depth = 0;

/** True while a setup-only Cognito session may exist. */
export function isTransientAuthSession(): boolean {
  return depth > 0;
}

/**
 * Run `fn` with the transient-session flag raised, lowering it again even when
 * `fn` throws: a failed password setup must not leave the app permanently
 * ignoring sign-ins.
 */
export async function withTransientAuthSession<T>(fn: () => Promise<T>): Promise<T> {
  depth += 1;
  try {
    return await fn();
  } finally {
    depth -= 1;
  }
}
